<template>
    <div class="student-portal-certificate" :class="{ dark: dark }">
        <v-container fluid class="pa-4">
            <v-row>
                <v-col cols="12">
                    <!-- Header with Back Button -->
                    <div class="mb-6 text-left">
                        <v-btn
                            variant="text"
                            prepend-icon="mdi-arrow-left"
                            @click="goBack"
                            class="back-btn mb-4"
                        >
                            Voltar aos Certificados
                        </v-btn>
                        <h1 class="card-title text-center">
                            Certificado do Curso
                        </h1>
                    </div>

                    <!-- Loading -->
                    <v-card v-show="isLoading" class="portal-certificate__card pa-8">
                        <v-row justify="center">
                            <v-col cols="auto">
                                <v-progress-circular
                                    indeterminate
                                    color="primary"
                                    size="64"
                                ></v-progress-circular>
                                <p class="text-center mt-4">
                                    Carregando certificado...
                                </p>
                            </v-col>
                        </v-row>
                    </v-card>

                    <!-- Certificate Content -->
                    <div v-show="!isLoading && selectedTemplate">
                        <v-card elevation="2" class="portal-certificate__card mx-auto">
                            <v-card-text class="pa-4 pa-md-6">
                                <!-- Action Buttons -->
                                <div class="text-center mb-6">
                                    <v-btn
                                        color="primary"
                                        variant="elevated"
                                        prepend-icon="mdi-download"
                                        @click="downloadCertificate"
                                        class="mb-4 mb-md-0 mr-md-4"
                                        size="large"
                                        :disabled="!canvasReady"
                                    >
                                        Baixar Certificado
                                    </v-btn>
                                    <v-btn
                                        color="secondary"
                                        variant="outlined"
                                        prepend-icon="mdi-printer"
                                        @click="printCertificate"
                                        size="large"
                                        :disabled="!canvasReady"
                                    >
                                        Imprimir
                                    </v-btn>
                                </div>

                                <!-- Certificate Canvas -->
                                <div class="text-center">
                                    <v-card
                                        variant="outlined"
                                        class="portal-certificate__card d-inline-block pa-md-4"
                                        style="background: #f5f5f5"
                                        max-width="960"
                                    >
                                        <canvas
                                            ref="certificateCanvas"
                                            :width="canvas.width"
                                            :height="canvas.height"
                                            class="certificate-canvas"
                                        ></canvas>
                                    </v-card>
                                </div>
                            </v-card-text>
                        </v-card>
                    </div>

                    <!-- Error State -->
                    <v-card
                        v-show="!isLoading && !selectedTemplate"
                        class="pa-8"
                    >
                        <v-row justify="center">
                            <v-col cols="auto" class="text-center">
                                <v-icon size="80" color="error" class="mb-4">
                                    mdi-alert-circle-outline
                                </v-icon>
                                <h3 class="text-h5 mb-2">
                                    Certificado não encontrado
                                </h3>
                                <p
                                    class="text-body-1 text-medium-emphasis mb-4"
                                >
                                    Não foi possível carregar o certificado para
                                    este curso.
                                </p>
                                <v-btn
                                    color="primary"
                                    variant="elevated"
                                    @click="goBack"
                                >
                                    Voltar aos Certificados
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>
    </div>
</template>

<script>
    import { mapState } from 'vuex';
    import { toast } from 'vue3-toastify';

    import apiCertificates from '@/utils/portal/certificate.js';

    import {
        certificateTemplates,
        canvasWidth,
        canvasHeight,
        logoWidth,
        logoHeight,
        signatureWidth,
        signatureHeight,
    } from '@/mocks/certificateTemplates.js';

    export default {
        name: 'CertificateViewer',
        data() {
            return {
                isLoading: false,
                canvasReady: false,
                canvas: {
                    ref: null,
                    width: canvasWidth,
                    height: canvasHeight,
                },
                templates: certificateTemplates,
                selectedTemplate: null,
                studentName: 'Nome do Aluno Completo',
                courseName: 'Nome do Curso',
                selectedLogo: {
                    file: null,
                    width: logoWidth,
                    height: logoHeight,
                },
                workload: {
                    enabled: true,
                    text: 'X horas.',
                },
                conclusionDate: {
                    enabled: true,
                    text: 'DATA DE CONCLUSÃO DO CURSO',
                },
                signature: {
                    enabled: true,
                    text: 'Assinatura',
                    file: null,
                    width: signatureWidth,
                    height: signatureHeight,
                },
            };
        },
        computed: {
            ...mapState(['token', 'dark']),
            courseId() {
                return this.$route.params.id;
            },
        },
        created() {
            this.loadCertificates();
        },
        mounted() {
            this.loadCanvas();
        },
        methods: {
            loadCanvas() {
                this.canvas.ref = this.$refs.certificateCanvas;
            },

            formatTime(seconds) {
                const hours = Math.floor(seconds / 3600);
                const minutes = Math.floor((seconds % 3600) / 60);
                
                if (hours > 0) {
                    return `${hours} horas e ${minutes} minutos`;
                } else {
                    return `${minutes} minutos`;
                }
            },            

            renderCertificate() {
                this.isLoading = true;
                this.canvasReady = false;
                const canvas = this.canvas.ref;

                if (!canvas || !this.selectedTemplate) {
                    toast.error('Erro ao renderizar o certificado.');
                    this.isLoading = false;
                    return;
                }

                const ctx = canvas.getContext('2d');
                ctx.clearRect(0, 0, canvas.width, canvas.height);

                // 1. Desenhar background
                const bgImg = new window.Image();

                bgImg.onload = () => {
                    ctx.drawImage(bgImg, 0, 0, canvas.width, canvas.height);

                    // 2. Escrever nome do estudante
                    this.writeOnCanvas(
                        ctx,
                        this.studentName,
                        this.selectedTemplate.studentNamePosition.x,
                        this.selectedTemplate.studentNamePosition.y,
                        this.selectedTemplate.fontStudentName.family,
                        this.selectedTemplate.fontStudentName.size,
                        this.selectedTemplate.fontStudentName.weight ??
                            undefined,
                        this.selectedTemplate.fontStudentName.color
                    );

                    //2.1. Escrever nome do curso
                    const courseName = 'Concluiu o curso ' + this.courseName;
                    this.writeOnCanvas(
                        ctx,
                        courseName,
                        this.selectedTemplate.workLoadPosition.x,
                        this.selectedTemplate.workLoadPosition.y,
                        this.selectedTemplate.fontWorkLoad.family,
                        this.selectedTemplate.fontWorkLoad.size,
                        this.selectedTemplate.fontWorkLoad.weight ?? undefined,
                        this.selectedTemplate.fontWorkLoad.color
                    );

                    // 2.2. Escrever carga horária (se habilitado)
                    if (this.workload.enabled) {
                        const worLoadText =
                            'com carga horária de' + ' ' + this.workload.text;
                        this.writeOnCanvas(
                            ctx,
                            worLoadText,
                            this.selectedTemplate.workLoadPosition.x,
                            this.selectedTemplate.workLoadPosition.y + 44,
                            this.selectedTemplate.fontWorkLoad.family,
                            this.selectedTemplate.fontWorkLoad.size,
                            this.selectedTemplate.fontWorkLoad.weight ??
                                undefined,
                            this.selectedTemplate.fontWorkLoad.color
                        );
                    }

                    // 2.3. Escrever data de conclusão (se habilitado)
                    if (this.conclusionDate.enabled && this.conclusionDate.text) {
                        this.writeOnCanvas(
                            ctx,
                            this.conclusionDate.text,
                            this.selectedTemplate.conclusionDatePosition.x,
                            this.selectedTemplate.conclusionDatePosition.y,
                            this.selectedTemplate.fontConclusionDate.family,
                            this.selectedTemplate.fontConclusionDate.size,
                            this.selectedTemplate.fontConclusionDate.weight ??
                                undefined,
                            this.selectedTemplate.fontConclusionDate.color
                        );
                    }

                    // 3. Desenhar logo (se houver)
                    if (this.selectedLogo.file) {
                        this.drawnOnCanvas(
                            ctx,
                            this.selectedLogo.file,
                            this.selectedTemplate.logoPosition.x,
                            this.selectedTemplate.logoPosition.y,
                            this.selectedLogo.width,
                            this.selectedLogo.height
                        );
                    }

                    // 4. Desenhar assinatura (se houver)
                    if (this.signature.enabled && this.signature.file) {
                        this.drawnOnCanvas(
                            ctx,
                            this.signature.file,
                            this.selectedTemplate.signaturePosition.x,
                            this.selectedTemplate.signaturePosition.y,
                            this.signature.width,
                            this.signature.height
                        );
                    } else if (this.signature.enabled && this.signature.text) {
                        this.writeOnCanvas(
                            ctx,
                            this.signature.text,
                            canvas.width / 2,
                            this.selectedTemplate.signaturePosition.y,
                            this.selectedTemplate.fontSignature.family,
                            this.selectedTemplate.fontSignature.size,
                            this.selectedTemplate.fontSignature.weight ??
                                undefined,
                            this.selectedTemplate.fontSignature.color
                        );
                    }

                    this.isLoading = false;
                    this.canvasReady = true;
                };

                bgImg.onerror = () => {
                    toast.error('Erro ao carregar template do certificado.');
                    this.isLoading = false;
                };

                bgImg.src = this.selectedTemplate.background;
            },

            drawnOnCanvas(ctx, file, x, y, width, height) {
                if (!ctx || !file) return;

                const img = new window.Image();
                img.crossOrigin = 'anonymous';

                img.onload = () => {
                    try {
                        ctx.drawImage(img, x, y, width, height);
                    } catch (error) {
                        toast.error(
                            'Erro ao desenhar imagem no canvas:',
                            error
                        );
                    }

                    // Libera o objeto URL se for File/Blob
                    if (file instanceof File || file instanceof Blob) {
                        URL.revokeObjectURL(img.src);
                    }
                };

                img.onerror = () => {
                    toast.error('Erro ao carregar imagem:', file.name || file);
                };

                if (typeof file === 'string') {
                    img.src = file;
                } else if (file instanceof File || file instanceof Blob) {
                    img.src = URL.createObjectURL(file);
                } else {
                    return;
                }
            },

            writeOnCanvas(
                ctx,
                text,
                x,
                y,
                fontFamily = 'Inter',
                fontSize = 20,
                fontWeight = 'normal',
                color = '#0000',
                textAlign = 'center'
            ) {
                if (!ctx) return;
                ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
                ctx.fillStyle = color;
                ctx.textAlign = textAlign;
                ctx.fillText(text, x, y);
            },

            downloadCertificate() {
                const canvas = this.$refs.certificateCanvas;
                if (!canvas || !this.canvasReady) {
                    toast.error(
                        'Certificado ainda não está pronto para download.'
                    );
                    return;
                }

                try {
                    canvas.toBlob(
                        (blob) => {
                            if (!blob) {
                                toast.error(
                                    'Erro ao gerar imagem do certificado.'
                                );
                                return;
                            }

                            const url = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            const now = new Date();
                            const dateStr = now.toISOString().split('T')[0];
                            const fileName = `certificado-${this.courseName
                                .replace(/[^a-z0-9]/gi, '-')
                                .toLowerCase()}-${dateStr}.png`;

                            link.href = url;
                            link.download = fileName;
                            link.style.display = 'none';

                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);

                            setTimeout(() => {
                                URL.revokeObjectURL(url);
                            }, 1000);

                            toast.success('Certificado baixado com sucesso!');
                        },
                        'image/png',
                        1.0
                    );
                } catch (error) {
                    toast.error('Erro ao baixar certificado.');
                }
            },

            printCertificate() {
                const canvas = this.$refs.certificateCanvas;
                if (!canvas || !this.canvasReady) {
                    toast.error(
                        'Certificado ainda não está pronto para impressão.'
                    );
                    return;
                }

                try {
                    // Converte canvas para imagem base64
                    const imgData = canvas.toDataURL('image/png', 1.0);

                    if (!imgData || imgData === 'data:,') {
                        toast.error('Erro ao processar imagem do certificado.');
                        return;
                    }

                    // Cria nova janela para impressão
                    const printWindow = window.open('', '_blank');

                    if (!printWindow) {
                        toast.error(
                            'Não foi possível abrir janela de impressão. Verifique se pop-ups estão bloqueados.'
                        );
                        return;
                    }

                    // HTML da página de impressão
                    const printContent = `
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <title>Certificado - ${this.courseName}</title>
                        <style>
                            @page {
                                size: A4 landscape;
                                margin: 0;
                            }
                            
                            * {
                                margin: 0;
                                padding: 0;
                                box-sizing: border-box;
                            }
                            
                            body {
                                width: 100vw;
                                height: 100vh;
                                display: flex;
                                justify-content: center;
                                align-items: center;
                                background: white;
                                font-family: Arial, sans-serif;
                            }
                            
                            .certificate-container {
                                width: 100%;
                                height: 100%;
                                display: flex;
                                justify-content: center;
                                align-items: center;
                                padding: 10mm;
                            }
                            
                            .certificate-image {
                                max-width: 100%;
                                max-height: 100%;
                                width: auto;
                                height: auto;
                                object-fit: contain;
                                border: none;
                            }
                            
                            @media print {
                                body {
                                    -webkit-print-color-adjust: exact;
                                    print-color-adjust: exact;
                                }
                                
                                .certificate-container {
                                    padding: 0;
                                }
                            }
                        </style>
                    </head>
                    <body>
                        <div class="certificate-container">
                            <img 
                                src="${imgData}" 
                                alt="Certificado do curso ${this.courseName}"
                                class="certificate-image"
                                onload="window.print(); setTimeout(() => window.close(), 1000);"
                                onerror="alert('Erro ao carregar certificado para impressão'); window.close();"
                            />
                        </div>
                    </body>
                    </html>
                `;

                    // Escreve o conteúdo na nova janela
                    printWindow.document.write(printContent);
                    printWindow.document.close();

                    // Foca na janela de impressão
                    printWindow.focus();

                    // toast.success('Abrindo janela de impressão...');
                } catch (error) {
                    toast.error('Erro ao preparar certificado para impressão.');
                }
            },

            async loadCertificates() {
                try {
                    this.isLoading = true;
                    const certificates =
                        await apiCertificates.getPortalCertificates(this.token);

                    const certificateData = certificates.find(
                        (template) => String(template.courseId) === String(this.courseId)
                    );

                    //ToDo: Verificar se o certificateData.certificate é diferente de 0 ou null
                    // if (certificateData && certificateData.certificate) {
                    if (certificateData) {
                        const foundTemplate = this.templates.find(
                            (tpl) =>
                                tpl.type === certificateData.certificateType
                        );
                        if (foundTemplate) {
                            this.selectedTemplate = foundTemplate;
                        }

                        this.studentName = certificateData.studentName;
                        this.courseName = certificateData.courseName;

                        if (certificateData.logoImageFinal) {
                            this.selectedLogo.file = certificateData.logoImageFinal;
                        }

                        this.workload.enabled =
                            !!certificateData.showCourseHours;
                        this.workload.text = this.formatTime(certificateData.lessonTime);

                        this.conclusionDate.enabled =
                            !!certificateData.showCompletionDate;
                        this.conclusionDate.text =
                            certificateData.dateCertificate;

                        this.signature.enabled =
                            !!certificateData.showSignature;
                        if (certificateData.signatureImageFinal) {
                            this.signature.file =
                                certificateData.signatureImageFinal;
                        }
                        if (certificateData.signatureName) {
                            this.signature.text = certificateData.signatureName;
                        }

                        this.$nextTick(() => {
                            this.renderCertificate();
                        });
                    }
                } catch (error) {
                    toast.error('Erro ao carregar os dados do certificado.');
                    throw error;
                } finally {
                    this.isLoading = false;
                }
            },

            goBack() {
                this.$router.push('/aluno/certificados');
            },
        },
    };
</script>

<style scoped>
    .student-portal-certificate {
        background-color: #f9f9f9fc;
        min-height: calc(100vh - 185px);
        flex: 1;
        height: 100%;
    }

    .certificate-canvas {
        width: 100%;
        height: auto;
        border: 1px solid #d6d8de;
        border-radius: 6px;
        background-color: #f5f5f5;
    }

    @media screen and (min-width: 960px) {
        .student-portal-certificate {
            padding: 50px 100px;
            min-height: calc(100vh - 120px);
        }
    }

    .dark.student-portal-certificate {
        background-color: #272727;
    }

    .dark.student-portal-certificate .portal-certificate__card {
        background-color: #121212;
        color: #ffffff;
    }

    .dark.student-portal-certificate .back-btn {
        color: #ffffff;
    }
</style>
